import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ReportIssueService } from '@data';
import { Button, SelectField, Status, TextField } from '@general';
import { yupResolver } from '@hookform/resolvers/yup';
import { IssueCreation, ReportErrorEntityType, ReportOptionType } from '@types';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ControllerPlus } from '@/components';
import { ModalContext } from '@/context';
import {
	hasServerErrorInForm,
	safeAnnotationTextErrorMessage,
	safeAnnotationTextRegExp,
	setServerErrorInForm,
} from '@/utils';

import styles from './reportForm.module.scss';

interface ReportFormProps {
	/**
	 * Options for dropdown list
	 */
	options?: ReportOptionType[];
	/**
	 * type of the error
	 */
	errorType: ReportErrorEntityType;
	/**
	 * id for the element (optional)
	 */
	itemId?: string | null;
}

interface ReportFormValues {
	issueType: string;
	issueDetails: string;
}

const reportFormSchema = yup.object().shape({
	issueType: yup.string().required('Please select an item'),
	issueDetails: yup
		.string()
		.required('Please complete this field')
		.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage),
});

const defaultValues: ReportFormValues = {
	issueType: undefined as unknown as string,
	issueDetails: undefined as unknown as string,
};

const defaultOptions: ReportOptionType[] = [
	{ option: 'Data shown is incorrect', noID: false },
	{ option: `Data is missing`, noID: false },
	{ option: 'Data shown is not mine', noID: false },
	{ option: 'Other', noID: false },
];

const DEVICE_FAULTY = 'Device is faulty';

/**
 * Report form
 */
export const ReportForm = ({ options = defaultOptions, errorType, itemId }: ReportFormProps) => {
	const { setShowDeviceFaultyFormModal, showReportIssueForm, setShowReportIssueForm } =
		useContext(ModalContext);
	const [isFormInvalid, setIsFormInvalid] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);

	// Form
	const form = useForm<ReportFormValues>({
		resolver: yupResolver(reportFormSchema),
		defaultValues,
	});

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isSubmitting },
	} = form;

	const issueType = watch('issueType');
	const issueDetails = watch('issueDetails');

	const formLabels = {
		issueType: 'What would you like to report?',
		issueDetails:
			issueType === DEVICE_FAULTY
				? 'Provide the following information'
				: 'Please provide more details.',
	};

	// Data
	const createReportIssue = useMutation({
		mutationFn: (reportIssueCreation: IssueCreation) =>
			ReportIssueService.createReportIssue(reportIssueCreation),
	});

	// Life cycle
	useEffect(() => {
		if (!showReportIssueForm) {
			reset();
			setIsFormInvalid(false);
			setShowSuccess(false);
		}
	}, [showReportIssueForm, reset]);

	// Show device faulty form modal when "Device is faulty" is selected
	useEffect(() => {
		if (issueType === DEVICE_FAULTY) {
			setShowDeviceFaultyFormModal(true);
		} else {
			setShowDeviceFaultyFormModal(false);
		}
	}, [issueType, setShowDeviceFaultyFormModal]);

	// Helpers
	const getPlaceholder = () => {
		if (errorType === 'Device') {
			return 'Include the Device reference or name.';
		}
		return 'E.g. Details should be XXXX';
	};

	const newIssueCreation = (data: ReportFormValues): IssueCreation => {
		const targetOption = options.find(optionItem => optionItem.option === data.issueType);
		const hasToAddEntityItem = !targetOption?.noID && itemId != null;
		return {
			message: data.issueDetails,
			field: data.issueType,
			entityType: errorType,
			...(hasToAddEntityItem && {
				entityId: itemId,
			}),
		};
	};

	const slideBackDrawer = () => {
		const timer = setTimeout(() => {
			setShowReportIssueForm(false);
			resetRequestStatus();
		}, 1500);
		return () => clearTimeout(timer);
	};

	const onSubmit = async (data: ReportFormValues) => {
		const issueData = newIssueCreation(data);
		await createReportIssue
			.mutateAsync(issueData)
			.then(() => {
				setShowSuccess(true);
				slideBackDrawer();
			})
			.catch(e => setServerErrorInForm(form, e)); // set server error if delete or create fails
	};

	const onInvalid = () => {
		setIsFormInvalid(true);
	};

	const resetRequestStatus = () => {
		createReportIssue.reset();
	};

	const closeDrawer = () => {
		setShowReportIssueForm(false);
		resetRequestStatus();
	};

	/**
	 * Clear alerts when form values change
	 */
	useEffect(() => {
		if (isFormInvalid || showSuccess) {
			setIsFormInvalid(false);
			setShowSuccess(false);
		}
	}, [issueType, issueDetails]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/*Only used for closing the report issue panel when clicking out */}
			{showReportIssueForm && <div className={styles['drawer-overlay']} onClick={closeDrawer} />}
			<div
				className={[
					styles['drawer-content-container'],
					showReportIssueForm && styles['drawer-content-container-opened'],
					!showReportIssueForm && styles['drawer-content-container-closed'],
				].join(' ')}
			>
				{showReportIssueForm && (
					<div
						onSubmit={handleSubmit(onSubmit, onInvalid)}
						className={[
							styles['drawer-content'],
							showReportIssueForm && styles['drawer-content-opened'],
							!showReportIssueForm && styles['drawer-content-closed'],
							showReportIssueForm && styles['drawer-content-scrollable'],
						].join(' ')}
						data-testid="reportIssue"
						style={{ WebkitOverflowScrolling: 'touch' }} // Ensure iOS Safari handles scrolling properly
					>
						<form className={styles['drawer-content-form']}>
							<p className={styles['drawer-content-title']}>Report issue</p>
							<ControllerPlus<ReportFormValues>
								hideRequired
								control={control}
								name="issueType"
								schema={reportFormSchema}
								labels={formLabels}
								disabled={isSubmitting}
								formValues={form.getValues()}
							>
								<SelectField
									options={options
										.filter(option => (itemId ? true : option.noID))
										.reduce<string[]>((acc, item) => {
											acc.push(item.option);
											return acc;
										}, [])}
									placeholder="Select your option"
									error={!!errors.issueType}
									testId="reportIssueSelector"
								/>
							</ControllerPlus>
							{issueType && issueType !== DEVICE_FAULTY && (
								<>
									<ControllerPlus<ReportFormValues>
										hideRequired
										control={control}
										name="issueDetails"
										schema={reportFormSchema}
										labels={formLabels}
										disabled={isSubmitting}
										formValues={form.getValues()}
									>
										<TextField
											type="textarea"
											placeholder={getPlaceholder()}
											status={errors.issueDetails ? 'error' : 'valid'}
											errorMsg={errors.issueDetails?.message}
											testId="reportIssueTextarea"
										/>
									</ControllerPlus>
									<div className={styles['drawer-content-status']}>
										<Status
											status={showSuccess ? 'success' : 'error'}
											show={!!isFormInvalid || showSuccess || hasServerErrorInForm(errors)}
											onClose={() => {
												setIsFormInvalid(false);
												setShowSuccess(false);
												form.clearErrors();
											}}
											msg={
												showSuccess
													? 'Issue reported'
													: hasServerErrorInForm(errors)
														? 'Report has failed due to server issue. Please try again later.'
														: 'Please resolve the errors above.'
											}
										/>
									</div>
									<div className={styles['drawer-content-button-container']}>
										<Button
											customStyles={[styles['drawer-content-button']].join(' ')}
											type="primary"
											size="small"
											onClick={handleSubmit(onSubmit, onInvalid)}
											isLoading={isSubmitting}
											disabled={createReportIssue.isSuccess}
											data-testid="reportIssueSubmit"
										>
											Submit
										</Button>
									</div>
								</>
							)}
						</form>
					</div>
				)}
			</div>
			<div
				onClick={() => setShowReportIssueForm(!showReportIssueForm)}
				className={[
					styles['drawer-button-container'],
					showReportIssueForm && styles['drawer-button-container-opened'],
				].join(' ')}
			>
				<div
					className={[
						styles['drawer-button'],
						showReportIssueForm && styles['drawer-button-opened'],
					].join(' ')}
					data-testid="reportIssueButton"
				>
					<span>Report issue</span>
				</div>
			</div>
		</>
	);
};
